<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="900px">
        <v-card>
            <v-card-title>
                <span class="text-h5">{{ $t('Valitse kirjastosta') }}</span>
                <v-spacer></v-spacer>
                <v-text-field v-model="search" append-icon="mdi-magnify" :label="$t('Hae')" single-line hide-details
                    dense outlined class="mt-0"></v-text-field>
            </v-card-title>

            <v-divider></v-divider>

            <v-card-text style="height: 500px; overflow-y: auto;">
                <!-- Loading State -->
                <div v-if="loading" class="text-center py-12">
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                </div>

                <!-- Empty State -->
                <div v-else-if="filteredImages.length === 0" class="text-center py-12">
                    <v-icon size="64" color="grey lighten-2">mdi-image-off</v-icon>
                    <p class="text-h6 grey--text mt-4">
                        {{ search ? $t('Ei hakutuloksia') : $t('Ei kuvia kirjastossa') }}
                    </p>
                </div>

                <!-- Image Grid -->
                <v-row v-else>
                    <v-col v-for="image in filteredImages" :key="image.id" cols="12" sm="6" md="4">
                        <v-card outlined hover @click="selectImage(image)"
                            :class="{ 'selected-image': selectedImage && selectedImage.id === image.id }">
                            <v-img :src="image.image.thumbnail || image.image" aspect-ratio="1.5"
                                class="grey lighten-4">
                                <template v-slot:placeholder>
                                    <v-row class="fill-height ma-0" align="center" justify="center">
                                        <v-progress-circular indeterminate color="grey lighten-1"></v-progress-circular>
                                    </v-row>
                                </template>
                            </v-img>
                            <v-card-subtitle class="pb-2">
                                <div class="text-truncate">{{ image.title }}</div>
                            </v-card-subtitle>
                        </v-card>
                    </v-col>
                </v-row>
            </v-card-text>

            <v-divider></v-divider>

            <!-- Pagination -->
            <v-card-actions v-if="pageCount > 1" class="justify-center">
                <v-pagination v-model="page" :length="pageCount" :disabled="loading" total-visible="7"></v-pagination>
            </v-card-actions>

            <v-divider></v-divider>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="grey" text @click="close" :disabled="adding">
                    {{ $t('Peruuta') }}
                </v-btn>
                <v-btn color="primary" @click="addToScreen" :loading="adding" :disabled="!selectedImage || adding">
                    {{ $t('Lisää näytölle') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api'
import adminApi from '@/api/admin'
import { useTranslations } from '@/@core/libs/i18n';
import { i18n } from '@/plugins/i18n';

export default {
    name: 'LibraryModal',
    props: {
        value: {
            type: Boolean,
            default: false
        },
        screenId: {
            type: [String, Number],
            required: true
        },
        position: {
            type: Number,
            required: true
        }
    },
    setup(props, { emit }) {
        const images = ref([])
        const loading = ref(false)
        const adding = ref(false)
        const selectedImage = ref(null)
        const search = ref('')

        const page = ref(1)
        const count = ref(0)
        const pageSize = 20
        const pageCount = computed(() => {
            if (!count.value) {
                return 1
            }
            return Math.ceil(count.value / pageSize)
        })

        const filteredImages = computed(() => {
            // Filtering is done server-side via ?search=...
            return images.value
        })

        // Fetch images when dialog opens
        watch(() => props.value, (newValue) => {
            if (newValue) {
                page.value = 1
                fetchImages()
            } else {
                // Reset on close
                selectedImage.value = null
                search.value = ''
                page.value = 1
                count.value = 0
                images.value = []
            }
        })

        // Server-side search
        watch(() => search.value, () => {
            if (props.value) {
                page.value = 1
                fetchImages()
            }
        })

        watch(() => page.value, () => {
            if (props.value) {
                fetchImages()
            }
        })

        const fetchImages = async () => {
            loading.value = true
            try {
                const response = await adminApi.getInfoTvContents({
                    page: page.value,
                    search: search.value || undefined,
                })
                if (response.ok) {
                    // Response is paginated: { count, next, previous, results }
                    count.value = response.data.count || 0
                    images.value = (response.data.results || []).filter(item => item.content_type === 2)
                }
            } catch (error) {
                console.error('Error fetching library images:', error)
            } finally {
                loading.value = false
            }
        }

        const selectImage = (image) => {
            selectedImage.value = image
        }

        const addToScreen = async () => {
            if (!selectedImage.value) return

            adding.value = true
            try {
                const response = await adminApi.saveInfoTvScreenContent(props.screenId, {
                    content_type: 2, // INFOTV_CONTENT_IMAGE
                    content_id: selectedImage.value.id,
                    position: props.position
                })

                if (response.ok) {
                    emit('added')
                    close()
                } else {
                    alert(response.data?.error || i18n.t('Virhe lisättäessä sisältöä'))
                }
            } catch (error) {
                console.error('Error adding content:', error)
                alert(error.response?.data?.error || i18n.t('Virhe lisättäessä sisältöä'))
            } finally {
                adding.value = false
            }
        }

        const close = () => {
            emit('input', false)
        }

        return {
            images,
            filteredImages,
            loading,
            adding,
            selectedImage,
            search,
            page,
            pageCount,
            selectImage,
            addToScreen,
            close
        }
    }
}
</script>

<style scoped>
.selected-image {
    border: 2px solid var(--v-primary-base) !important;
    box-shadow: 0 0 10px rgba(33, 150, 243, 0.3);
}
</style>
